import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Checkbox } from '../ui/checkbox';
import { Button } from '../ui/button';
import { Sparkle, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Rendu markdown minimal (aligne avec BorisChantierPage.js pour cohérence)
function MarkdownView({ content }) {
  if (!content) return null;
  const html = content
    .replace(/^### (.*)$/gm, '<h3 class="text-sm font-bold mt-2 mb-1 text-purple-900">$1</h3>')
    .replace(/^## (.*)$/gm, '<h2 class="text-base font-bold mt-3 mb-1.5 text-purple-900">$1</h2>')
    .replace(/\*\*(.*?)\*\*/g, '<strong class="text-purple-900">$1</strong>')
    .replace(/^\- (.*)$/gm, '<li class="ml-4 list-disc">$1</li>')
    .replace(/^([0-9]+)\. (.*)$/gm, '<li class="ml-4 list-decimal">$2</li>')
    .replace(/`([^`]+)`/g, '<code class="bg-purple-100 px-1 rounded text-xs">$1</code>')
    .replace(/\n\n/g, '</p><p class="mt-1.5">');
  return <div className="text-xs text-gray-800 leading-relaxed"
    dangerouslySetInnerHTML={{ __html: `<p>${html}</p>` }} />;
}

/**
 * GabyPhotoAnalyzer
 * ================
 * Composant reutilisable pour proposer une analyse Gaby sur des photos deja
 * uploadees (file_id). Utilise dans les formulaires CSPS (Reserves, Securite,
 * Inspection, QuickAddFAB).
 *
 * Props :
 *  - photos: [{ id, filename?, url? }]  liste des photos uploadees
 *  - chantierId: str
 *  - notesContext?: str  contexte optionnel (ex: titre de la reserve)
 *  - variant?: 'compact' | 'full'  compact = juste boutons, full = auto-checkbox+resultats inline
 *
 * L'utilisateur controle : soit tout analyser (checkbox globale), soit
 * photo par photo via un petit bouton violet.
 */
export function GabyPhotoAnalyzer({ photos, chantierId, notesContext = '', variant = 'full' }) {
  const [enabled, setEnabled] = useState(false);  // acces Gaby ?
  const [analyses, setAnalyses] = useState({});   // {file_id: {loading, result, error}}
  const [analyzeAll, setAnalyzeAll] = useState(false);

  // Verifie que l'utilisateur a acces a Gaby (feature flag CSPS)
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/boris/status`, { withCredentials: true });
        setEnabled(!!(r.data?.enabled_for_me || r.data?.enabled));
      } catch { setEnabled(false); }
    })();
  }, []);

  const analyzeOne = useCallback(async (fileId) => {
    setAnalyses(prev => ({ ...prev, [fileId]: { loading: true } }));
    try {
      const r = await axios.post(`${API}/boris/analyze-file`, {
        file_id: fileId, chantier_id: chantierId, notes: notesContext,
      }, { withCredentials: true });
      setAnalyses(prev => ({ ...prev, [fileId]: { loading: false, result: r.data.result_markdown } }));
      toast.success('Analyse Gaby prête');
    } catch (e) {
      const msg = e.response?.data?.detail || 'Analyse échouée';
      setAnalyses(prev => ({ ...prev, [fileId]: { loading: false, error: msg } }));
      toast.error(msg);
    }
  }, [chantierId, notesContext]);

  // Analyse automatique quand analyzeAll est coche + nouvelle photo
  useEffect(() => {
    if (!analyzeAll || !enabled) return;
    photos.forEach(p => {
      if (!analyses[p.id]) analyzeOne(p.id);
    });
  }, [analyzeAll, photos, enabled, analyzeOne, analyses]);

  if (!enabled) return null;
  if (!photos || photos.length === 0) return null;

  if (variant === 'compact') {
    // Juste un bouton "Analyser toutes les photos"
    return (
      <div className="flex items-center gap-2 mt-2">
        <Sparkle size={14} className="text-purple-600" />
        <span className="text-xs text-purple-700 font-medium">
          {photos.length} photo{photos.length > 1 ? 's' : ''} à analyser avec Gaby ?
        </span>
        <Button size="sm" variant="outline" className="h-7 text-xs border-purple-300 text-purple-700 hover:bg-purple-50"
          onClick={() => photos.forEach(p => analyzeOne(p.id))}
          disabled={photos.every(p => analyses[p.id])}
          data-testid="gaby-analyze-all-compact">
          Analyser
        </Button>
      </div>
    );
  }

  return (
    <div className="mt-3 space-y-2" data-testid="gaby-photo-analyzer">
      {/* Case globale + explication */}
      <label className="flex items-start gap-2 p-3 bg-gradient-to-r from-purple-50 to-fuchsia-50 border border-purple-200 rounded-lg cursor-pointer hover:border-purple-300">
        <Checkbox checked={analyzeAll} onCheckedChange={v => setAnalyzeAll(!!v)}
          className="mt-0.5" data-testid="gaby-analyze-all-toggle" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <Sparkle size={14} className="text-purple-600" />
            <span className="text-sm font-semibold text-purple-900">
              Analyser toutes mes photos avec <span className="italic">Gaby</span>
            </span>
          </div>
          <p className="text-xs text-purple-700/80 mt-0.5">
            Gaby détecte les risques CSPS et rédige une observation prête à valider. Vous pouvez aussi analyser photo par photo ci-dessous.
          </p>
        </div>
      </label>

      {/* Boutons individuels + resultats */}
      {photos.map(p => {
        const a = analyses[p.id];
        return (
          <div key={p.id} className="border border-gray-200 rounded-lg p-2 bg-white">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs text-gray-600 truncate flex-1">
                📷 {p.filename || p.original_filename || `Photo ${p.id.slice(0, 8)}`}
              </span>
              {a?.result ? (
                <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                  ✓ Analysée
                </span>
              ) : (
                <Button size="sm" variant="outline"
                  onClick={() => analyzeOne(p.id)}
                  disabled={a?.loading}
                  className="h-7 text-xs border-purple-300 text-purple-700 hover:bg-purple-50"
                  data-testid={`gaby-analyze-${p.id}`}>
                  {a?.loading ? <Loader2 size={12} className="animate-spin mr-1" /> : <Sparkle size={12} className="mr-1" />}
                  {a?.loading ? 'Gaby analyse…' : 'Analyser avec Gaby'}
                </Button>
              )}
            </div>
            {a?.result && (
              <div className="mt-2 p-3 bg-purple-50 border-l-4 border-purple-400 rounded">
                <MarkdownView content={a.result} />
              </div>
            )}
            {a?.error && (
              <div className="mt-2 text-xs text-red-600 bg-red-50 p-2 rounded">
                ⚠️ {a.error}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
